'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CompareContext = createContext(null);

const MAX_COMPARE = 4;
const STORAGE_KEY = 'petmarket_compare';
const COMPARE_VERSION_KEY = 'petmarket_compare_version';
const COMPARE_STORAGE_VERSION = 1;

export const CompareProvider = ({ children }) => {
  const [compareList, setCompareList] = useState(() => {
    try {
      const version = localStorage.getItem(COMPARE_VERSION_KEY);
      if (Number(version) !== COMPARE_STORAGE_VERSION) {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.setItem(COMPARE_VERSION_KEY, String(COMPARE_STORAGE_VERSION));
        return [];
      }
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(compareList));
    localStorage.setItem(COMPARE_VERSION_KEY, String(COMPARE_STORAGE_VERSION));
  }, [compareList]);

  const addToCompare = useCallback((product) => {
    setCompareList((prev) => {
      if (prev.length >= MAX_COMPARE) return prev;
      if (prev.some((p) => p._id === product._id)) return prev;
      return [...prev, product];
    });
  }, []);

  const removeFromCompare = useCallback((productId) => {
    setCompareList((prev) => prev.filter((p) => p._id !== productId));
  }, []);

  const clearCompare = useCallback(() => {
    setCompareList([]);
  }, []);

  const isInCompare = useCallback(
    (productId) => compareList.some((p) => p._id === productId),
    [compareList]
  );

  return (
    <CompareContext.Provider
      value={{
        compareList,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isInCompare,
        maxCompare: MAX_COMPARE,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) throw new Error('useCompare must be used within CompareProvider');
  return context;
};
